/**
 * Service for fetching drivetime isochrone data and aggregations
 */

import { getValidAuthHeaders } from '../utils/authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface DrivetimeAggregationRequest {
  file: string;
  version: number;
  filters?: Record<string, any>;
  field?: string;
}

export interface DrivetimeRequest {
  lat: number;
  lng: number;
  times: number[];
  aggs?: Record<string, DrivetimeAggregationRequest>;
}

export interface RadiusRequest {
  lat: number;
  lng: number;
  mode: 'radius';
  radius: number;
  steps: number;
  aggs: Record<string, DrivetimeAggregationRequest>;
}

export interface DrivetimePolygon {
  type: string;
  coordinates: number[][][];
}

export interface DrivetimeResponse {
  lat: number;
  lng: number;
  cached: boolean;
  isochrones: Record<number, DrivetimePolygon | null>;
  aggs?: Record<string, Record<number, number | null>>;
  dedupStatus?: 'processed' | 'cached' | 'unknown';
}

export interface RadiusResponse {
  lat: number;
  lng: number;
  mode: 'radius';
  radius: number;
  steps: number;
  aggs: Record<string, Record<number, number | null>>;
  dedupStatus?: 'processed' | 'cached' | 'unknown';
}

/**
 * Fetch drivetime isochrones and optional aggregations
 */
export const fetchDrivetime = async (
  request: DrivetimeRequest
): Promise<DrivetimeResponse> => {
  try {
    const url = `${API_BASE_URL}/catchment-aggregations`;
    
    console.log('[DrivetimeService] Sending request:', {
      url,
      body: request,
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: await getValidAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      console.error('[DrivetimeService] API error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });
      
      throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Extract deduplication status from response headers
    const dedupStatus = response.headers.get('X-Dedup-Status') as 'processed' | 'cached' | null;
    if (dedupStatus) {
      data.dedupStatus = dedupStatus;
    }
    
    console.log('[DrivetimeService] Response received:', {
      ...data,
      dedupStatus: dedupStatus || 'unknown',
    });
    
    return data;
  } catch (error) {
    console.error('[DrivetimeService] Failed to fetch drivetime data', error);
    throw error;
  }
};

/**
 * Fetch radius-based aggregations (no isochrone polygons, uses Haversine distance)
 */
export const fetchRadius = async (
  request: RadiusRequest
): Promise<RadiusResponse> => {
  try {
    const url = `${API_BASE_URL}/catchment-aggregations`;
    
    console.log('[DrivetimeService] Sending radius request:', {
      url,
      body: request,
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: await getValidAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      console.error('[DrivetimeService] Radius API error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });
      
      throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    const dedupStatus = response.headers.get('X-Dedup-Status') as 'processed' | 'cached' | null;
    if (dedupStatus) {
      data.dedupStatus = dedupStatus;
    }
    
    console.log('[DrivetimeService] Radius response received:', data);
    
    return data;
  } catch (error) {
    console.error('[DrivetimeService] Failed to fetch radius data', error);
    throw error;
  }
};

export const drivetimeService = {
  fetchDrivetime,
  fetchRadius,
};
